import { formatDate } from '@angular/common';
import { LOCALE_ID, ViewChild } from '@angular/core';
import { Component, Inject, OnInit } from '@angular/core';
import { NgbAccordion } from '@ng-bootstrap/ng-bootstrap';
import { AgGridAngular } from 'ag-grid-angular';
import { GridOptions } from 'ag-grid-community';
import { DashboardService } from 'src/app/dashboard/dashboard.service';
import { SystemTemplateService } from 'src/app/dashboard/system-templates/system-template.service';
import { CvAngularLoadingOverlayComponent } from 'src/app/reusables/ag-grid-components/cv-angular-loading-overlay/cv-angular-loading-overlay.component';
import { AgGridHelperService } from 'src/app/reusables/services/ag-grid-helper.service';
import { CvConfigurationService } from 'src/app/reusables/services/cv-configuration.service';
import { DashboardResponsiveApiModelSparse, SystemTemplateWto, WorkspaceNewTabViewMode, WorkspaceTemplateViewMode } from 'src/shared/models/dashboard.model';

@Component({
    selector: 'cv-open-existing-template',
    templateUrl: './open-existing-template.component.html',
    styleUrls: ['./open-existing-template.component.scss']
})
export class OpenExistingTemplateComponent implements OnInit {
    @ViewChild('acc') accordionComponent: NgbAccordion;
    @ViewChild('sharedGrid') agGridShared: AgGridAngular;
    @ViewChild('systemTemplateGrid') agGridSystemTemplates: AgGridAngular;

    filterValue = '';
    systemTemplateCrudAccess = false;
    noTemplates = false;
    useLoading = false;
    isRemoving = false;
    templatesLoading = false;
    systemTemplatesLoading = false;
    hasLoaded = false;
    showSharedTemplates = true;
    showSystemTemplates = true;

    gridOptionsShared: GridOptions;
    gridOptionsSystem: GridOptions;

    templates: DashboardResponsiveApiModelSparse[] = [];
    systemTemplates: SystemTemplateWto[] = [];

    constructor(
        @Inject(LOCALE_ID) locale: string,
        public dashboardService: DashboardService,
        private agGridHelperService: AgGridHelperService,
        private systemTemplateService: SystemTemplateService,
        private config: CvConfigurationService,
    ) {
        this.gridOptionsShared = agGridHelperService.createGridOptions({
            columnDefs: [
                { headerName: "Name", field: "DashboardName", pinned: false },
                { headerName: "Created By", field: "SharedBy", pinned: false },
                {
                    headerName: "Last Modified",
                    field: "LastModifiedDate",
                    pinned: false,
                    valueFormatter: params => formatDate(params.value, CVAngularDateTimeFormat, locale),
                },
            ],
            components: {
                cvAngularLoadingComponent: CvAngularLoadingOverlayComponent,
            },
            loadingOverlayComponent: 'cvAngularLoadingComponent',
            getRowId: ({data: rowData}) => rowData.DashboardID.toString(),
            rowSelection: 'single',
            suppressRowClickSelection: true,
            suppressCellFocus: true,
            suppressContextMenu: false,
            defaultColDef: {
                sortable: true,
                filter: true,
                resizable: true,
                suppressMovable: true,
                suppressMenu: true,
            },
            animateRows: true,
            rowHeight: 32,
            isExternalFilterPresent: () => this.filterValue?.length > 0,
            doesExternalFilterPass: node => agGridHelperService.doesExternalFilterPass(node, this.filterValue),
            onGridReady: params => {
                params.columnApi.applyColumnState({ state: [{ colId: 'DashboardName', sort: 'asc' }] });
            },
            onGridSizeChanged: params => {
                if (this.accordionComponent.isExpanded('shared-template-panel'))
                    params.api.sizeColumnsToFit();
            },
            onRowClicked: params => {
                this.agGridSystemTemplates?.api?.deselectAll();
                params.node.setSelected(!params.node.isSelected());

                if (params.node.isSelected()) {
                    this.selectSharedTemplate(params.node.data);
                } else {
                    this.dashboardService.selectedTemplate = null;
                }
            },
        });

        this.gridOptionsSystem = agGridHelperService.createGridOptions({
            columnDefs: [
                { headerName: "Name", field: "SystemTemplateName", pinned: false },
                { headerName: "Group", field: "SystemTemplateGroup", pinned: false },
                { headerName: "Integrations", field: "Integrations", hide: true, lockVisible: true },
                { headerName: "Personas", field: "Personas", hide: true, lockVisible: true },
                {
                    headerName: "Last Used",
                    field: "LastUsedDate",
                    pinned: false,
                    valueFormatter: params => formatDate(params.value, CVAngularDateTimeFormat, locale),
                },
            ],
            components: {
                cvAngularLoadingComponent: CvAngularLoadingOverlayComponent,
            },
            loadingOverlayComponent: 'cvAngularLoadingComponent',
            getRowId: ({ data: rowData }) => {
                return rowData.SystemTemplateID.toString()
            },
            rowSelection: 'single',
            suppressRowClickSelection: true,
            suppressCellFocus: true,
            suppressContextMenu: false,
            defaultColDef: {
                sortable: true,
                filter: true,
                resizable: true,
                suppressMovable: true,
                suppressMenu: true,
            },
            animateRows: true,
            rowHeight: 32,
            isExternalFilterPresent: () => this.filterValue?.length > 0,
            doesExternalFilterPass: node => agGridHelperService.doesExternalFilterPass(node, this.filterValue),
            onGridReady: params => {
                params.api.applyColumnState({ state: [{colId: 'SystemTemplateName', sort: 'asc'}]});
                if (this.systemTemplateCrudAccess) {
                    params.api.setColumnsVisible(["Integrations", "Personas"], true);
                }
            },
            onGridSizeChanged: params => {
                if (this.accordionComponent.isExpanded('system-template-panel'))
                    params.api.sizeColumnsToFit();
            },
            onRowClicked: params => {
                this.agGridShared?.api?.deselectAll();
                params.node.setSelected(!params.node.isSelected());

                if (params.node.isSelected()) {
                    this.selectSystemTemplate(params.node.data);
                } else {
                    this.dashboardService.selectedSystemTemplate = null;
                }
            },
        });
    }

    async ngOnInit() {
        const sharedPromise = this.loadTemplates();
        const systemPromise = this.loadSystemTemplates();

        // load system template crud access
        const response = await this.systemTemplateService.hasSystemTemplateAdminAccess();
        this.systemTemplateCrudAccess = response;
        if (this.systemTemplateCrudAccess) {
            this.agGridSystemTemplates?.api?.setColumnsVisible(["Integrations", "Personas"], true);
        }

        await sharedPromise;
        await systemPromise;
        this.hasLoaded = true;

        requestAnimationFrame(() => { // wait for ngIf to update with the accordion
            if (this.systemTemplates.length > 0)
                this.accordionComponent.expand('system-template-panel');
            if (this.templates.length > 0)
                this.accordionComponent.expand('shared-template-panel');
        });
    }

    async loadTemplates() {
        this.templatesLoading = true;
        this.agGridShared?.api?.showLoadingOverlay();
        try {
            this.templates = await this.dashboardService.loadSharedTemplates();
            this.noTemplates = this.templates.length === 0 && this.systemTemplates.length === 0;
        } finally {
            this.templatesLoading = false;
            this.agGridShared?.api?.hideOverlay();
        }
    }

    async loadSystemTemplates(forceReload = false) {
        this.systemTemplatesLoading = true;
        this.agGridSystemTemplates?.api?.showLoadingOverlay();
        try {
            this.systemTemplates = await this.dashboardService.loadSystemTemplates(forceReload, this.config.isCxOne);
            this.noTemplates = this.templates.length === 0 && this.systemTemplates.length === 0;
        } finally {
            this.systemTemplatesLoading = false;
            this.agGridSystemTemplates?.api?.hideOverlay();
        }
    }

    getSharedHeight() {
        return Math.min(600, (this.templates.length * 32) + 35) + 'px';
    }

    getSystemHeight() {
        return Math.min(600, (this.systemTemplates.length * 32) + 35) + 'px';
    }

    async selectSystemTemplate(template: SystemTemplateWto) {
        this.dashboardService.previewType = 'system';
        this.dashboardService.selectedTemplate = null;
        this.dashboardService.selectedSystemTemplate = template;
    }

    async selectSharedTemplate(dashboard: DashboardResponsiveApiModelSparse) {
        this.dashboardService.previewType = 'shared';
        this.dashboardService.selectedSystemTemplate = null;
        this.dashboardService.selectedTemplate = dashboard;
    }

    async useTemplate() {
        if (this.dashboardService.previewType === 'shared') {
            this.dashboardService.newTabSelectionView = WorkspaceNewTabViewMode.Templates;
            this.dashboardService.templateView = WorkspaceTemplateViewMode.NewTemplate;
        } else {
            const key = this.dashboardService.selectedSystemTemplate.SystemTemplateKey;
            try {
                this.useLoading = true;
                const dashboard = await this.dashboardService.copyTemporaryDashboardFromSystemTemplate(key);
                this.dashboardService.loadExistingDashboardsAndGroups();
                this.dashboardService.selectedSystemTemplate = null;
                await this.dashboardService.addDashboardToWorkspace(this.dashboardService.workspaceKey, dashboard, true);
            } finally {
                this.useLoading = false;
                this.backToLanding();
            }
        }
    }

    async removeTemplate() {
        this.isRemoving = true;
        try {
            await this.systemTemplateService.removeSystemTemplate(this.dashboardService.selectedSystemTemplate.SystemTemplateKey);
            this.dashboardService.selectedSystemTemplate = null;
            await this.loadSystemTemplates(true);
        } finally {
            this.isRemoving = false;
        }
    }

    onFilterChange() {
        this.agGridShared?.api?.onFilterChanged();
        this.agGridSystemTemplates?.api?.onFilterChanged();
    }

    backToLanding() {
        this.dashboardService.newTabSelectionView = WorkspaceNewTabViewMode.Landing;
    }
}
